/**
 * Document status machine — ONE enum for every document type, with the legal transitions declared
 * per type and enforced by the engine.
 *
 * Four statuses, because that is what the flow has. seedtrac's transactions are forms: you fill one,
 * Preview it, and Submit it. The eight-status version this replaces was tracking a warehouse
 * workflow — gate, weigh each bag, QA sample, complete unloading, SLOC desk, approve — that seedtrac
 * does not have and operators did not want.
 *
 *     draft ──preview──▶ previewed ──submit──▶ submitted
 *       │                    │                     │
 *       └────────────────────┴──────cancel─────────┴──▶ cancelled
 *
 * `previewed` is a real state, not a modal: seedtrac's Preview is a committing step you can come
 * back from, and the arrival list has a Preview tab. Editing a previewed document returns it to
 * draft, so what was reviewed is always what gets submitted.
 */
export const DOC_TYPES = [
  "arrival",
  "processing",
  "dispatch",
  "packaging",
] as const;
export type DocType = (typeof DOC_TYPES)[number];

export const DOC_STATUSES = [
  "draft",
  "previewed",
  "submitted",
  "cancelled",
] as const;
export type DocStatus = (typeof DOC_STATUSES)[number];

/**
 * How far the PHYSICAL work on a bag-wise arrival has got — setup, unloading, SLOC.
 *
 * Not a lifecycle. `DOC_STATUSES` above is what the document IS (draft → previewed → submitted);
 * these three say how much of the truck has actually been dealt with, and they advance
 * independently. seedtrac encodes the same three as 0 / 2 / 1, where "complete" sorts between
 * "not started" and "in progress" — readable names, in a sane order, are the whole point of
 * restating them here. The `stage_state` pgEnum is this list.
 */
export const STAGE_STATES = ["pending", "in_progress", "complete"] as const;
export type StageState = (typeof STAGE_STATES)[number];

/**
 * Where the truck of a submitted Dispatch Stock Transfer has got to.
 *
 * Not a lifecycle either: the STDN stays `submitted`, and this is read off the gate records at
 * both ends. Final Submit means the lots are loaded but the truck is still inside the plant; the
 * sending plant's Gate Pass-Out puts it on the road; the receiving plant's Gate Pass-In means it
 * has been delivered. Neither gate record can be undone once the next one exists, so the stage
 * only ever moves forward.
 */
export const TRANSFER_STAGES = ["loaded", "in_transit", "delivered"] as const;
export type TransferStage = (typeof TRANSFER_STAGES)[number];

export const TRANSFER_STAGE_LABELS: Record<TransferStage, string> = {
  loaded: "Loaded",
  in_transit: "In Transit",
  delivered: "Delivered",
};

/**
 * The Dispatch Stock Transfer list's tabs: the paperwork states either side of Final Submit, and the
 * truck's stage in between (`submitted` never has a tab of its own).
 */
export const TRANSFER_TABS = [
  "draft",
  "previewed",
  ...TRANSFER_STAGES,
  "cancelled",
] as const;
export type TransferTab = (typeof TRANSFER_TABS)[number];

export const transferStage = (t: {
  status: DocStatus;
  gatePassOutNo: string | null;
  gatePassInNo: string | null;
}): TransferStage | null =>
  t.status !== "submitted"
    ? null
    : t.gatePassInNo
      ? "delivered"
      : t.gatePassOutNo
        ? "in_transit"
        : "loaded";

/**
 * Why a preview stopped being valid.
 *
 * A previewed arrival returns to `draft` the moment anything is written to it, so what was reviewed
 * is always what gets submitted. That rule is old; what is new is saying WHY out loud. Once the
 * handheld syncs asynchronously, a desk can be half-way through checking an arrival when a late
 * sack lands underneath them, and "it vanished from my queue" is not an acceptable explanation.
 *
 * The reviewer's own return to draft (a send-back for reweigh) is deliberately NOT in this list:
 * telling someone their own click invalidated something is noise.
 */
export const PREVIEW_INVALIDATION_REASONS = [
  "bags_changed",
  "sloc_changed",
  "lots_changed",
  "details_changed",
] as const;
export type PreviewInvalidationReason =
  (typeof PREVIEW_INVALIDATION_REASONS)[number];

/** The sentence the reviewer reads. Here, so the two clients cannot word it differently. */
export const PREVIEW_INVALIDATION_MESSAGES: Record<
  PreviewInvalidationReason,
  string
> = {
  bags_changed: "Preview invalidated — unloading data changed after preview.",
  sloc_changed:
    "Preview invalidated — storage locations changed after preview.",
  lots_changed:
    "Preview invalidated — the lots on this arrival changed after preview.",
  details_changed: "Preview invalidated — the arrival changed after preview.",
};

/**
 * A lot the desk's checker sent back for reweighing (`checkStep` forms), kept on the line as
 * `document_lines.extra.reweigh`. Its bags stay (user, 2026-10-24): the floor checks each on the scale
 * and reweighs the wrong ones bag by bag. The flag stays until the floor previews the unloading again,
 * which asks for a confirm when no bag of the lot changed.
 */
export interface ReweighRequest {
  reason: string;
  /** The checker's full name. */
  by: string;
  /** ISO instant. */
  at: string;
}

/** The line's reweigh request, or null. Tolerant of whatever else `extra` holds. */
export function reweighRequestOf(
  extra: Record<string, unknown> | null | undefined
): ReweighRequest | null {
  const r = extra?.reweigh as Partial<ReweighRequest> | undefined;
  return r && typeof r.reason === "string"
    ? { reason: r.reason, by: String(r.by ?? ""), at: String(r.at ?? "") }
    : null;
}

/** Named actions an actor performs; each maps to exactly one edge per document type. */
export const DOC_ACTIONS = [
  "create",
  "preview",
  "edit",
  "submit",
  "cancel",
] as const;
export type DocAction = (typeof DOC_ACTIONS)[number];

export interface Transition {
  from: DocStatus;
  to: DocStatus;
  action: DocAction;
}
const t = (from: DocStatus, action: DocAction, to: DocStatus): Transition => ({
  from,
  action,
  to,
});

/** Terminal statuses: nothing moves out of them. */
export const TERMINAL_STATUSES: ReadonlySet<DocStatus> = new Set<DocStatus>([
  "cancelled",
]);

/** Statuses in which the document counts as final and its lots exist. */
export const COMMITTED_STATUSES: ReadonlySet<DocStatus> = new Set<DocStatus>([
  "submitted",
]);

/** Statuses whose lines may still be edited. */
export const OPEN_STATUSES: ReadonlySet<DocStatus> = new Set<DocStatus>([
  "draft",
  "previewed",
]);

/**
 * Every type runs the same five edges. They are spelled out per type rather than shared by
 * reference because `TRANSITIONS` is the place a future type WOULD diverge — a packaging run that
 * cannot be cancelled once submitted, say — and a shared array would make that change look like an
 * edit to every type at once.
 */
const formEdges = (): Transition[] => [
  t("draft", "preview", "previewed"),
  // Any edit to a previewed document sends it back, so Preview always shows what will be submitted.
  t("previewed", "edit", "draft"),
  t("previewed", "submit", "submitted"),
  t("draft", "cancel", "cancelled"),
  t("previewed", "cancel", "cancelled"),
  t("submitted", "cancel", "cancelled"),
];

export const TRANSITIONS: Record<DocType, Transition[]> = {
  arrival: formEdges(),
  // The cob chain's middle legs: blending, drying & shelling, and SLOC updation. One doc type,
  // three form codes — `docs/PLAN.md` §5.3 reserves `processing` for exactly this.
  processing: formEdges(),
  // Dispatch to one of our plants. N documents per chain, one per truck.
  dispatch: formEdges(),
  // The WB Packing Slip (packing work order): Release is the submit edge; it moves no stock.
  packaging: formEdges(),
};

export function findTransition(
  docType: DocType,
  from: DocStatus,
  action: DocAction
): Transition | undefined {
  return TRANSITIONS[docType].find(
    (x) => x.from === from && x.action === action
  );
}

export function allowedActions(docType: DocType, from: DocStatus): DocAction[] {
  return TRANSITIONS[docType]
    .filter((x) => x.from === from)
    .map((x) => x.action);
}

export function allowedStatuses(docType: DocType): DocStatus[] {
  const s = new Set<DocStatus>();
  for (const x of TRANSITIONS[docType]) {
    s.add(x.from);
    s.add(x.to);
  }
  return [...s];
}
